import { API_PREFIX, ROUTES } from '@/config/const';
import { APP_ENV } from '@/config/env';
import { removeTokens, useAuthContext } from '@/contexts/AuthContext';
import fetcher from '@/utils/fetcher';
import { useNavigate } from 'react-router-dom';

const useApiRequest = () => {
  const { getValidAccessToken } = useAuthContext();
  const navigate = useNavigate();

  const getAuthHeaders = async () => {
    const token = await getValidAccessToken();
    if (!token) throw new Error('Unauthorized');
    return {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
      Authorization: `Bearer ${token}`,
    };
  };

  const handleUnauthorized = (error: any) => {
    if (error?.message === 'Unauthorized') {
      removeTokens();
      navigate(ROUTES.login);
      return null;
    }
    throw error;
  };

  const sendData = async ({
    url,
    payload,
    method = 'POST',
  }: {
    url: string;
    payload?: any;
    method?: 'POST' | 'PATCH' | 'DELETE';
  }) => {
    try {
      const headers = await getAuthHeaders();

      const requestOptions: RequestInit = {
        method,
        headers,
        ...(payload && { body: JSON.stringify(payload) }),
      };

      const response = await fetch(`${APP_ENV.apiUrl}/${API_PREFIX}/${url}`, requestOptions);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.message || 'Sorry, something went wrong.');
      }

      const data = response.status !== 204 ? await response.json() : null;
      return data;
    } catch (error: any) {
      return handleUnauthorized(error);
    }
  };

  const fetchData = async (url: string) => {
    try {
      const token = await getValidAccessToken();
      if (!token) throw new Error('Unauthorized');
      return await fetcher(url, token);
    } catch (error: any) {
      return handleUnauthorized(error);
    }
  };

  return { sendData, fetchData };
};

export default useApiRequest;
